/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React from "react";
import { DatePicker } from "@mui/x-date-pickers";
import {
  Alert,
  Autocomplete,
  AutocompleteRenderInputParams,
  TextField, Grid,
} from "@mui/material";
import moment from "moment";
import { Button } from "components/haven-design/buttons";
import { Input } from "components/haven-design/form/inputs";
import { Modal } from "components/haven-design/modal";
import StructuredReasons from "components/structured-reasons";
import { InterestStage, STATUSES_REASON_LABEL } from "model/interest/status";
import { DialogActions, DialogContent } from "components/haven-design/modal/dialog";
import styles from "./actions.module.css";
import { dateToString, skipReasons, unitSizes } from "./helpers";

type Props = {
  onRequestClose: () => void;
  onSubmit: () => void;
  onChange: (value: string) => void;
  onCancel: () => void;
  isOpen: boolean;
  isSubmitting: boolean;
  newStatus: Action | BulkAction | null;
  currentStatus: Action | BulkAction;
  reason: string;
  bulkAction?: boolean;
  selectedCount?: number;
}

export enum Action {
  AddDocument = "Add Document",
  AddNote = "Add Note",
  ChangeStatus = "Change Status",
  LogCall = "Log Call",
  MovedIn = "Moved In",
  OnWaitlist = "On Waitlist",
  Removed = "Removed",
  ResetPassword = "Reset Password",
  RequestForms = "Request Forms",
  SendFullApp = "Send Full Application",
  SendMessage = "Send Message",
  Skip = "Skip",
  SyncWithOneSite = "Sync with OneSite"
}

export enum BulkAction {
  Star = "Star",
  Unstar = "Unstar",
  MovedIn = "Moved In",
  OnWaitlist = "On Waitlist",
  Removed = "Removed",
  Skip = "Skip",
  AddNote = "Add Note",
}

const statusIsActive = (status: Action | BulkAction) =>
  status !== Action.MovedIn && status !== Action.Removed;

const displayConfirmationTitle = (
  currentStatus: Action | BulkAction,
  newStatus: Action | BulkAction,
  bulkAction?: boolean,
  selectedCount?: number,
) => {
  const reinstateMsg = "Why are you reinstating this applicant?";

  if (!statusIsActive(currentStatus) && statusIsActive(newStatus)) {
    return reinstateMsg;
  }
  switch (newStatus) {
    case Action.Removed:
      return STATUSES_REASON_LABEL[InterestStage.Removed];
    case Action.MovedIn:
      return STATUSES_REASON_LABEL[InterestStage.MovedIn];
    case Action.OnWaitlist:
      return reinstateMsg;
    case Action.Skip:
      if (bulkAction) {
        return "Why are you skipping these applicants?";
      }
      return "Why are you skipping this applicant?";
    case BulkAction.AddNote: {
      const n = selectedCount ?? 0;
      return n === 1 ? "Add Note to 1 application" : `Add Note to ${n} applications`;
    }

    default:
      return "Status Change";
  }
};

const displayPlaceholder = (status: Action | BulkAction) => {
  switch (status) {
    case Action.OnWaitlist:
    case Action.Removed:
    case Action.Skip:
      return "Enter a reason...";
    case Action.MovedIn:
      return "Unit number, move in date, etc...";
    case BulkAction.AddNote:
      return "Enter note text...";
    default:
      return "";
  }
};

const ModalTitleText = ({ status }: { status: Action | BulkAction }) => (
  <>
    Changing the status to <strong>{status}</strong>&nbsp;
    requires a reason for auditing purposes.
  </>
);

const ReasonModal: React.FC<Props> = ({
  onRequestClose,
  onSubmit,
  onChange,
  onCancel,
  isOpen,
  currentStatus,
  newStatus,
  reason,
  isSubmitting,
  bulkAction,
  selectedCount,
}) => {
  const [isValid, setIsValid] = React.useState(reason !== "");
  const [actionDate, setActionDate] = React.useState<Date | null>(new Date());
  const [optionalDate, setOptionalDate] = React.useState<Date | null>(null);
  const [isSkipValid, setIsSkipValid] = React.useState(false);
  const [autocompleteValue, setAutocompleteValue] =
    React.useState<typeof skipReasons[number] | null>(null);
  const [optionalValue, setOptionalValue] = React.useState("");
  const emptyInputs = () => {
    setActionDate(new Date());
    setOptionalDate(null);
    onChange("");
    setIsSkipValid(false);
    setAutocompleteValue(null);
    setOptionalValue("");
  };
  const onSubmitWrapper = () => {
    onSubmit();
    emptyInputs();
  };
  const onCloseWrapper = () => {
    onRequestClose();
    emptyInputs();
  };
  React.useEffect(() => {
    if (!autocompleteValue) {
      onChange("");
      setIsSkipValid(false);
      return;
    }
    const actionDateString = moment(actionDate).format("MM/DD/YYYY");
    let optionalValueText = optionalValue;
    if (autocompleteValue.date) optionalValueText = dateToString(optionalDate);
    optionalValueText = optionalValueText ? `, ${optionalValueText}` : "";
    onChange(autocompleteValue.value ? `${actionDateString} - ${autocompleteValue.value}${optionalValueText}` : "");
    let skipValid = true;
    if (!autocompleteValue.value || Object.keys(autocompleteValue.value).length === 0) {
      skipValid = false;
    }
    if (autocompleteValue.required && !optionalValueText) skipValid = false;
    if (!actionDate) skipValid = false;

    setIsSkipValid(skipValid);
  }, [autocompleteValue, optionalValue, optionalDate, actionDate]);

  const handleRemovalReasonOnChange = (value: string) => {
    setIsValid(value !== "");
    onChange(value);
  };

  return (
    <Modal
      title={newStatus && displayConfirmationTitle(currentStatus, newStatus, bulkAction, selectedCount)}
      isOpen={isOpen}
      onRequestClose={onCloseWrapper}
      fullWidth
    >
      <DialogContent>
        {newStatus === Action.Skip && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 5 }}>
              <DatePicker
                slotProps={{
                  textField: {
                    style: { minWidth: "15em" },
                    required: true,
                    autoComplete: "off",
                    variant: "outlined",
                    label: "Skip Date",
                  },
                  inputAdornment: { position: "end" },
                }}
                disableFuture
                closeOnSelect
                format="MM/dd/yyyy"
                value={actionDate}
                onChange={setActionDate}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 7 }}>
              <Autocomplete
                id="skip-reason-input"
                options={skipReasons}
                getOptionLabel={({ value }: {value: string}) => value}
                onChange={(_event: any, data: any) => {
                  setAutocompleteValue(data);
                  setOptionalValue(data?.date ? dateToString(new Date()) : "");
                }}
                renderInput={(params: AutocompleteRenderInputParams) => <TextField
                  required
                  label="Skip Reason"
                  {...params} />}
              />
            </Grid>
            {autocompleteValue?.text && (<Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={autocompleteValue.text}
                placeholder={autocompleteValue.text}
                required={autocompleteValue.required}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  setOptionalValue(e.target.value);
                }}/>
            </Grid>)}
            {autocompleteValue?.unitSize && (<Grid size={{ xs: 12 }}>
              <Autocomplete
                options={unitSizes}
                onChange={(_event: any, value: string | null) => {
                  setOptionalValue(value ?? "");
                }}
                renderInput={(params: AutocompleteRenderInputParams) => <TextField
                  required={autocompleteValue.required}
                  label={autocompleteValue.unitSize}
                  {...params} />}
              />
            </Grid>)}
            {autocompleteValue?.date && (<Grid size={{ xs: 12, sm: 5 }}>
              <DatePicker
                slotProps={{
                  textField: {
                    style: { minWidth: "15em" },
                    autoComplete: "off",
                    variant: "outlined",
                    label: autocompleteValue.date,
                  },
                  inputAdornment: { position: "end" },
                }}
                closeOnSelect
                format="MM/dd/yyyy"
                value={optionalDate}
                disablePast
                onChange={setOptionalDate}
              />
            </Grid>)}
          </Grid>
        )}
        {newStatus === BulkAction.AddNote && (
          <Input
            name="note"
            label="Note"
            placeholder={displayPlaceholder(newStatus)}
            value={reason}
            onChange={(event) => {
              const { value } = event.target;
              setIsValid(value.length >= 1 && value.length <= 5000);
              onChange(value);
            }}
            disabled={isSubmitting}
            required
            multiline
            minRows={4}
            error={reason.length > 5000}
            helperText={reason.length > 5000 ? "Note must be 5000 characters or less" : undefined}
          />
        )}
        {newStatus && ![Action.Removed, Action.Skip, BulkAction.AddNote].includes(newStatus as any) && (
          <>
            <ModalTitleText status={newStatus} />
            <Input
              name="reason"
              type="text"
              label="Reason"
              placeholder={displayPlaceholder(newStatus)}
              value={reason}
              onChange={(event) => onChange(event.target.value)}
              disabled={isSubmitting}
              required={true}
            />
          </>
        )}

        {newStatus === Action.Removed && (
          <StructuredReasons
            disabled={isSubmitting}
            label="Removal Reason*"
            onChange={handleRemovalReasonOnChange}
          />
        )}
      </DialogContent>
      {isSubmitting && newStatus === Action.Skip && bulkAction && (
        <Alert style={{ marginTop: 5 }} severity="info">
            Skipping applicants. This may take a while.
        </Alert>
      )}
      <DialogActions>
        <Button
          className={styles.confirmationButton}
          color="secondary"
          size="S"
          onClick={onCancel}
          disabled={isSubmitting}
        >
            Cancel
        </Button>
        <Button
          className={styles.confirmationButton}
          color="primary"
          size="S"
          disabled={isSubmitting || (
            newStatus === Action.Removed && !isValid) || (
            newStatus === Action.Skip && !isSkipValid) || (
            newStatus === BulkAction.AddNote && !isValid)}
          onClick={onSubmitWrapper}
        >
          {isSubmitting ? "Submitting" : "Confirm"}
        </Button>
      </DialogActions>
    </Modal>
  );
};
export default ReasonModal;
